import { orderSelectors } from 'ducks/orderComponents'
import { orgSelectors } from 'ducks/orgs'
import type { DistributorDataTypeV2 } from 'elements/hardwareSelectorV2/types'
import type { QuoteProjectDataType } from 'hardware/types'
import type OrderLineItem from 'pages/ordering/OrderLineItem'
import type { ProjectInfo } from 'pages/ordering/orderByProjectCart/useOrderByProjectCartData'
import type { HardwareSupplierFilterKeyType, LineItemStatus, ProjectOrderDetail } from 'pages/ordering/type'
import { getResolvedSource, isManualSource, type ManualSourceType } from 'pages/ordering/utils'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import restClient from 'restClient'
import type { ComponentTypes, ComponentTypesV2 } from 'types/selectComponent'
import type { RootState } from 'types/state'

const restClientInstance = restClient(window.API_ROOT + '/api')

/** Resolved source type from getResolvedSource */
type ResolvedSourceType = ReturnType<typeof getResolvedSource>

/**
 * Cart table row model. Shaped for the Cart table and summary panel.
 * Built from OrderLineItem(s) during consolidation—not a domain entity.
 */
export type ConsolidatedLineItem = {
  uuid: string
  code: string
  quantity: number
  status: LineItemStatus
  componentType?: ComponentTypes
  componentTypeV2?: ComponentTypesV2
  rating?: number
  orderingData?: DistributorDataTypeV2[]
  projectOrder?: ProjectOrderDetail[]
  selectedDistributor: HardwareSupplierFilterKeyType | 'component' | undefined
  hasMixedSources: boolean
  isManualCost: boolean
  resolvedSource?: ResolvedSourceType
  componentTitle?: string
}

export function toConsolidatedLineItem(
  lineItem: OrderLineItem,
  options: {
    hasMixedSources: boolean
    isSingleProject: boolean
    quantity?: number
    projectOrder?: ProjectOrderDetail[]
  }
): ConsolidatedLineItem {
  const quantity = options.quantity ?? lineItem.quantity
  const projectOrder = options.projectOrder ?? lineItem.projectOrder
  // Use the actual selectedDistributor from the line item, even if there were mixed sources initially.
  // The hasMixedSources flag indicates the initial state, but if a source has been selected,
  // we should use it for validation purposes (e.g., enabling checkout buttons).
  const selectedDistributor = lineItem.selectedDistributor
  const resolvedSource =
    getResolvedSource(selectedDistributor, lineItem.orderingData, {
      hasMixedSources: options.hasMixedSources,
      isSingleProject: options.isSingleProject,
      isManualCost: isManualSource(lineItem.selectedDistributor),
    }) ??
    // Fallback: when getResolvedSource returns undefined (e.g. empty orderingData), use manual source directly
    (selectedDistributor && isManualSource(selectedDistributor) ? selectedDistributor : undefined)
  return {
    uuid: lineItem.uuid,
    code: lineItem.code,
    quantity,
    status: lineItem.status,
    componentType: lineItem.componentType,
    componentTypeV2: lineItem.componentTypeV2,
    rating: lineItem.rating,
    orderingData: lineItem.orderingData,
    projectOrder,
    selectedDistributor,
    hasMixedSources: options.hasMixedSources,
    isManualCost: isManualSource(lineItem.selectedDistributor),
    resolvedSource,
    componentTitle: lineItem.componentTitle, // "title" data field of the component - is only populated by "other" type components
  }
}

function collectSourcesPerComponent(
  orderableLineItems: OrderLineItem[]
): Map<string, Set<HardwareSupplierFilterKeyType | ManualSourceType | undefined>> {
  const sourcesPerComponent = new Map<string, Set<HardwareSupplierFilterKeyType | ManualSourceType | undefined>>()
  orderableLineItems.forEach((lineItem) => {
    const key = lineItem?.code
    if (key == null) return
    if (!sourcesPerComponent.has(key)) {
      sourcesPerComponent.set(key, new Set())
    }
    sourcesPerComponent.get(key)!.add(lineItem.selectedDistributor)
  })
  return sourcesPerComponent
}

/**
 * One row per component code, merged quantities and project orders (same rules as useCartData consolidation).
 */
export function consolidateLineItemsByCode(
  orderableLineItems: OrderLineItem[],
  isSingleProject: boolean
): { consolidatedLineItems: ConsolidatedLineItem[]; hasMixedSourceItems: boolean } {
  const itemsMap = new Map<string, ConsolidatedLineItem>()
  const sourcesPerComponent = collectSourcesPerComponent(orderableLineItems)
  let foundMixedSources = false

  orderableLineItems.forEach((lineItem) => {
    const key = lineItem?.code
    if (key == null) return
    const sources = sourcesPerComponent.get(key)
    if (!sources) return
    const hasMixedSources = sources.size > 1

    if (hasMixedSources) {
      foundMixedSources = true
    }

    if (itemsMap.has(key)) {
      const existing = itemsMap.get(key)!
      const existingPo = Array.isArray(existing.projectOrder) ? existing.projectOrder : []
      const itemPo = Array.isArray(lineItem.projectOrder) ? lineItem.projectOrder : []
      const mergedProjectOrder: ProjectOrderDetail[] =
        itemPo.length > 0 ? [...existingPo, ...itemPo] : (existing.projectOrder ?? [])
      itemsMap.set(key, {
        ...existing,
        quantity: existing.quantity + lineItem.quantity,
        projectOrder: mergedProjectOrder,
      })
    } else {
      try {
        itemsMap.set(
          key,
          toConsolidatedLineItem(lineItem, {
            hasMixedSources,
            isSingleProject,
          })
        )
      } catch {
        // Skip malformed line items so we don't break the cart page
      }
    }
  })

  return {
    consolidatedLineItems: Array.from(itemsMap.values()),
    hasMixedSourceItems: foundMixedSources,
  }
}

/**
 * Hook to fetch and organize data for the Cart page.
 * - Gets confirmed line items from Redux (same as global cart drawer/badge)
 * - Consolidates items by component code into a single table view
 * - Fetches project addresses for display when projectOrder is present
 *
 * Uses getConfirmedLineItems (not getOrderableLineItems) so the Cart Page shows
 * the same items as the global cart icon/drawer. Items added from Project BOM
 * may not pass the "orderable" stock filter but should still appear in the cart.
 */
export const useCartData = () => {
  const orgId = useSelector(orgSelectors.getOrg)?.id
  const confirmedLineItems = useSelector(orderSelectors.getConfirmedLineItems)
  const orderableLineItems = useMemo(() => {
    const list = Array.isArray(confirmedLineItems) ? confirmedLineItems : []
    return list.filter((li) => li && li.status !== 'loading')
  }, [confirmedLineItems])

  const [projectInfoMap, setProjectInfoMap] = useState<Record<number, ProjectInfo>>({})
  const [isLoadingProjects, setIsLoadingProjects] = useState(false)
  const [systemIds, setSystemIds] = useState<number[]>([])

  const fetchedProjectIdsRef = useRef<string>('')

  // Subscribe directly to quantity and distributor changes so we re-render when they change.
  // The cached getConfirmedLineItems selector can return the same array ref when objects
  // are mutated in place, which prevents useSelector from triggering re-renders.
  const quantitySignature = useSelector((state: RootState) => {
    const items = state.orderComponents.lineItems.filter((item) => item.confirmed)
    return items.map((li) => `${li.uuid}:${li.quantity}`).join('|')
  })
  const distributorSignature = useSelector((state: RootState) => {
    const items = state.orderComponents.lineItems.filter((item) => item.confirmed)
    return items.map((li) => `${li.uuid}:${li.selectedDistributor ?? ''}`).join('|')
  })

  const { consolidatedLineItems, hasMixedSourceItems } = useMemo(() => {
    const projectIds = [
      ...new Set(
        orderableLineItems.flatMap((li) =>
          Array.isArray(li.projectOrder) ? li.projectOrder.map((po) => po?.projectId).filter(Boolean) : []
        )
      ),
    ]
    const isSingleProject = projectIds.length === 1
    return consolidateLineItemsByCode(orderableLineItems, isSingleProject)
  }, [quantitySignature, distributorSignature, orderableLineItems])

  const projectIds = useMemo(() => {
    const ids = new Set<number>()
    orderableLineItems.forEach((lineItem) => {
      lineItem.projectOrder?.forEach((po) => ids.add(po.projectId))
    })
    return Array.from(ids)
  }, [orderableLineItems])

  const isSingleProject = projectIds.length === 1
  const projectIdsKey = [...projectIds].sort().join(',')

  const supplierAssistProjectData = useMemo((): QuoteProjectDataType[] => {
    const sortedProjectIds = [...projectIds].sort((a, b) => a - b)
    return sortedProjectIds.map((projectId) => {
      const systems = (projectInfoMap[projectId]?.systems ?? [])
        .map((system) => {
          if (!system?.uuid) return null
          const displayName =
            system.name && system.name.trim() ? system.name : `${system.module_quantity ?? 0} Panels System`
          return { uuid: system.uuid, name: displayName }
        })
        .filter((s): s is { uuid: string; name: string } => s != null)

      return {
        id: projectId,
        address: projectInfoMap[projectId]?.address ?? '',
        systems,
      }
    })
  }, [projectIds, projectInfoMap])

  useEffect(() => {
    if (!orgId || projectIds.length === 0) return
    const hasNewProjects = projectIds.some((id) => !projectInfoMap[id])
    if (!hasNewProjects) return
    if (fetchedProjectIdsRef.current === projectIdsKey) return

    fetchedProjectIdsRef.current = projectIdsKey
    setIsLoadingProjects(true)

    restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${orgId}/projects/?fields=id,address,business_name,installation_date,systems&filter=${projectIds.join(',')}`,
    })
      .then(({ data }) => {
        const newProjectInfoMap: Record<number, ProjectInfo> = {}
        data?.forEach((project: ProjectInfo) => {
          newProjectInfoMap[project.id] = project
        })
        setProjectInfoMap(newProjectInfoMap)
      })
      .catch((error) => {
        console.error('Failed to fetch project info:', error)
      })
      .finally(() => {
        setIsLoadingProjects(false)
      })
  }, [orgId, projectIds, projectIdsKey, projectInfoMap])

  useEffect(() => {
    const systemIds = new Set<number>()
    orderableLineItems.forEach((lineItem) => {
      lineItem.projectOrder?.forEach((po) => {
        if (po.systemId) {
          systemIds.add(po.systemId)
        }
      })
    })
    setSystemIds(Array.from(systemIds))
  }, [orderableLineItems])

  return {
    consolidatedLineItems,
    projectInfoMap,
    systemIds,
    supplierAssistProjectData,
    isLoading: isLoadingProjects,
    hasLineItems: orderableLineItems.length > 0,
    isSingleProject,
    hasMixedSourceItems,
  }
}

export default useCartData
